import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LuEye, LuEyeOff, LuKeyRound, LuArrowLeft, LuTriangleAlert, LuCircleCheck } from "react-icons/lu";
import XiLogo from "../components/ui/XiLogo";
import useTranslation from '../hooks/useTranslation';

/**
 * Password reset landing page.
 *
 * Presentation only — the reset endpoint, payload, validation rules and
 * redirect timing are unchanged. The form now uses the shared Xi auth chrome
 * and accessible labels/status regions.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!email || !token) {
      setMessage('Invalid reset link. Please request a new password reset.');
    }
  }, [email, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!newPassword || !confirmPassword) {
      setMessage('Please enter both passwords.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      const base = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000' : 'https://app.neurolonic.com/api');

      const setResponse = await fetch(`${base}/mobile/reset-password/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reset_token: token, new_password: newPassword })
      });

      const setData = await setResponse.json();

      if (setResponse.ok) {
        setSuccess(true);
        setMessage('Password reset successfully! Redirecting to login...');
        setTimeout(() => navigate('/'), 3000);
      } else {
        setMessage(setData.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const linkUsable = Boolean(email && token);

  return (
    <div className="xi-public xi-enroll xi-reset">
      <div className="xi-public__veil" aria-hidden="true" />
      <div className="xi-public__body xi-enroll__state-body">
        <div className="xi-enroll__brand">
          <XiLogo size="sm" tagline />
        </div>

        <div className="xi-auth-card xi-enroll__card">
          <header className="xi-auth-head">
            <span className="xi-eyebrow">
              <LuKeyRound aria-hidden="true" /> Account security
            </span>
            <h1>{t('Reset Your Password')}</h1>
            <p>{t('Enter your new password below')}</p>
          </header>

          {message ? (
            <div
              className={`xi-alert ${success ? 'xi-alert--ok' : 'xi-alert--bad'}`}
              role={success ? 'status' : 'alert'}
              aria-live="polite"
            >
              {success ? <LuCircleCheck aria-hidden="true" /> : <LuTriangleAlert aria-hidden="true" />}
              <div className="xi-alert__body">
                <p className="xi-alert__text">{message}</p>
              </div>
            </div>
          ) : null}

          {!success && linkUsable ? (
            <form className="xi-auth-form" onSubmit={handleSubmit}>
              <div className="xi-field">
                <label className="xi-label" htmlFor="reset-new-password">
                  {t('New Password')}
                </label>
                <div className="xi-password">
                  <input
                    id="reset-new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('Enter new password')}
                    className="xi-input"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="xi-password__toggle"
                    aria-label={showPassword ? t('Hide password') : t('Show password')}
                  >
                    {showPassword ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div className="xi-field">
                <label className="xi-label" htmlFor="reset-confirm-password">
                  {t('Confirm Password')}
                </label>
                <input
                  id="reset-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('Confirm new password')}
                  className="xi-input"
                  autoComplete="new-password"
                  required
                />
              </div>

              <button
                type="submit"
                className="xi-btn xi-btn--primary xi-btn--lg xi-btn--block"
                disabled={loading}
              >
                {loading ? t('Resetting...') : t('Reset Password')}
              </button>
            </form>
          ) : null}

          {!success && !linkUsable ? (
            <p className="xi-enroll__note">
              {t('Open the link from your reset email to continue.')}
            </p>
          ) : null}

          <div className="xi-auth-foot">
            <button type="button" onClick={() => navigate('/')}>
              <LuArrowLeft aria-hidden="true" /> {t('Back to Login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
